import { useEffect, useState } from 'react';
import { cn } from '../../../../../../lib/utils';
import { primaryText, secondaryText, semiBoldText, smallTextSize } from '../../../../../../styling/stylingHelper';
import {
  getAICheckerLineSetDivergences,
  type LineSetDivergencesResult,
} from '../../../../../../services/admin/adminService';
import type { TFn } from '../../types';

// Tab 5: pakbon/factuur LINE-SET divergences. Only same-file pairs (shared message_id) are audited —
// there the two sides were built from one document and must match, so any difference means the file
// was extracted twice with different results (typically an "Add missing factuur" replay). Empty IS
// the happy path. Lazily mounted like Tab 3 (the query scans same-file pairs in the range).
export function AICheckerLineSetDivergences({ startDate, endDate, onCount, t }: { startDate: string; endDate: string; onCount: (n: number) => void; t: TFn }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<LineSetDivergencesResult | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const r = await getAICheckerLineSetDivergences(startDate, endDate);
        if (!cancelled) { setData(r); onCount(r.divergences.length); }
      } catch (e: any) {
        if (!cancelled) setError(e.message || 'Failed to load line-set divergences');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [startDate, endDate, onCount]);

  if (loading) return <div className={cn(secondaryText, smallTextSize, 'text-center py-8')}>{t('adminTesting.review.divergenceLoading')}</div>;
  if (error) return <div className={cn('text-center py-8 text-red-500 dark:text-textRed-dark', smallTextSize)}>{error}</div>;
  if (!data || data.divergences.length === 0) {
    return <div className={cn(secondaryText, smallTextSize, 'text-center py-8')}>🎉 {t('adminTesting.review.divergenceEmpty')}</div>;
  }

  return (
    <div className="space-y-4">
      <p className={cn(secondaryText, 'text-[11px] italic')}>{t('adminTesting.review.divergenceCaption')}</p>
      {data.divergences.map((d) => (
        <div key={d.factuurOrderId} className="border border-gray-200 dark:border-gray-600 rounded-lg p-3">
          <div className="flex flex-wrap items-baseline justify-between gap-2 mb-2">
            <div>
              <span className={cn(primaryText, semiBoldText, 'text-xs font-mono')}>{d.platform}</span>
              <span className={cn(secondaryText, 'text-xs')}> · {d.restaurantName}</span>
              <span className={cn(secondaryText, 'text-[10px] font-mono ml-2')}>
                {t('adminTesting.review.divergenceOrderLabel')} {d.orderNumber} · {t('adminTesting.review.divergenceInvoiceLabel')} {d.invoiceNumber} · {d.orderDate}
              </span>
            </div>
            <div className="flex items-center gap-3">
              <span className={cn(secondaryText, 'text-[10px] font-mono')}>
                {t('adminTesting.review.divergenceLineCounts', { pakbon: d.pakbonLineCount, factuur: d.factuurLineCount })}
              </span>
              <span className={cn('text-xs font-mono font-semibold', d.valueDelta >= 0 ? 'text-red-600 dark:text-red-400' : 'text-amber-600 dark:text-amber-400')}>
                {d.valueDelta >= 0 ? '+' : ''}€{d.valueDelta.toFixed(2)}
              </span>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className={cn(secondaryText, 'border-b border-gray-200 dark:border-gray-600')}>
                  <th className="text-left py-1 px-2">{t('adminTesting.review.divergenceColSide')}</th>
                  <th className="text-left py-1 px-2">{t('adminTesting.review.divergenceColProduct')}</th>
                  <th className="text-right py-1 px-2">{t('adminTesting.review.divergenceColQuantity')}</th>
                  <th className="text-right py-1 px-2">{t('adminTesting.review.divergenceColPrice')}</th>
                  <th className="text-right py-1 px-2">{t('adminTesting.review.divergenceColValue')}</th>
                </tr>
              </thead>
              <tbody>
                {[
                  ...d.onlyInFactuur.map((l) => ({ line: l, side: 'factuur' as const })),
                  ...d.onlyInPakbon.map((l) => ({ line: l, side: 'pakbon' as const })),
                ].map(({ line, side }, li) => (
                  <tr key={`${side}-${line.matchKey}-${li}`} className={cn('border-b border-gray-100 dark:border-gray-700', li % 2 === 0 ? 'bg-gray-50/50 dark:bg-gray-800/30' : '')}>
                    <td className="py-1 px-2">
                      <span className={cn('px-1.5 py-0.5 rounded text-[10px] font-medium whitespace-nowrap',
                        side === 'factuur'
                          ? 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300'
                          : 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300')}>
                        {side === 'factuur' ? t('adminTesting.review.divergenceOnlyInFactuur') : t('adminTesting.review.divergenceOnlyInPakbon')}
                      </span>
                    </td>
                    <td className={cn(primaryText, 'py-1 px-2 max-w-md')} title={line.productName}>
                      <div className="truncate">{line.productName}</div>
                      <div className={cn(secondaryText, 'font-mono text-[10px]')}>{line.articleId || line.matchKey}</div>
                    </td>
                    <td className={cn(secondaryText, 'py-1 px-2 text-right font-mono')}>{line.quantity}</td>
                    <td className={cn(secondaryText, 'py-1 px-2 text-right font-mono')}>€{line.price.toFixed(2)}</td>
                    <td className={cn(primaryText, 'py-1 px-2 text-right font-mono')}>€{line.value.toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}
    </div>
  );
}
